import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, useBreakpointValue, useColorModeValue, Button, Center, Spinner } from "@chakra-ui/react";
import { useDealFinderTickers } from "hooks/useDealFinderTickers";
import { useButtonProps, useCollectionKey, useDashboardMetricsData } from "hooks";
import { BlockSectionHeader } from "screens/content/contentView/previewSection/BlockSectionHeader";
import { SectionContentLayout } from "../../../components/utils";
import { SectionFooter } from "../../CommonComponents/Sections";
import QuadrantChart from "screens/landing/tabs/collections/chartComponents/QuadrantChart/QuadrantChart";
import { CustomVerticalBarChart, getScoreColor } from "screens/landing/tabs/collections/chartComponents/CustomVerticalBarChart";
import { Array } from "runtypes";
import { Score } from "api/tickers/models/Score";
import type { DealFinderTicker } from "api/tickers/models/DealFinderTicker";
import { SectionWrapper } from "../../CommonComponents/Sections/SectionWrapper";
import SectionInvestmentSummary from "../../CommonComponents/Sections/SectionContents/SectionInvestmentSummary";
import SectionInvestorReadiness from "../../CommonComponents/Sections/SectionContents/SectionInvestorReadiness";
import SectionNetworkActivity from "../../CommonComponents/Sections/SectionContents/SectionNetworkActivity";

interface Props {
  collectionId: string;
  isLoading?: boolean;
}

export const CompanyDetailViewDashboardTab: React.FC<Props> = ({ collectionId, isLoading = false }) => {
  const [tickerData, setTickerData] = useState<DealFinderTicker | null>(null);

  const primaryButtonProps = useButtonProps("sm", "primary");

  const { getDealFinderTickers } = useDealFinderTickers();
  const ticker = useCollectionKey(collectionId, "ticker");
  const exchange = useCollectionKey(collectionId, "stockExchange");

  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const bgColor = useColorModeValue("white", "#161B25");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  // Fetch ticker data
  useEffect(() => {
    const fetchTickerData = async () => {
      if (ticker && exchange) {
        try {
          const data = await getDealFinderTickers({
            symbolWithExchange: `${exchange}:${ticker}`,
          });
          setTickerData(data.length > 0 ? data[0] : null);
        } catch (error) {
          console.error("Error fetching ticker data:", error);
          setTickerData(null);
        }
      }
    };

    fetchTickerData();
  }, [ticker, exchange, getDealFinderTickers]);

  // Create single project data for quadrant chart using ticker data
  const singleProjectData = useMemo(() => {
    if (!tickerData) return undefined;

    const xValue = tickerData.investmentScoreAnalytical ? tickerData.investmentScoreAnalytical * 10 : undefined;
    const yValue = tickerData.investmentScoreSentiment ? tickerData.investmentScoreSentiment * 10 : undefined;

    return {
      x: xValue ? { value: xValue.toString() } : undefined,
      y: yValue ? { value: yValue.toString() } : undefined,
      projectName: tickerData.name,
      id: tickerData.id,
      companyLogoUrl: tickerData.companyLogoUrl || "",
      companyDescription: tickerData.investmentPotential || tickerData.companyDescription || "",
    };
  }, [tickerData]);

  // Get quadrant data using the dashboard metrics hook
  const { quadrantData } = useDashboardMetricsData("pi_quadrant_index", undefined, singleProjectData);

  // Mock investment summary data (would come from ticker data in real implementation)
  const investmentSummaryData = useMemo(() => {
    if (tickerData) {
      return {
        totalTransactions: 68,
        runningTotalRaised: "$7.4M",
        mostRecentValuation: "$32.6M",
        totalShares: "18,390,068",
        lastSharePrice: "$1.77",
        totalInvestors: 58,
      };
    }
    // Mock data when no real data available
    return {
      totalTransactions: 68,
      runningTotalRaised: "$7.4M",
      mostRecentValuation: "$32.6M",
      totalShares: "18,390,068",
      lastSharePrice: "$1.77",
      totalInvestors: 58,
    };
  }, [tickerData]);

  // Mock data for sections that can't be populated with real data
  const mockInvestorReadinessScore = 34;

  const mockNetworkActivityData = [
    { date: "Day 1", searches: 45, views: 25, pins: 2 },
    { date: "Day 2", searches: 52, views: 28, pins: 3 },
    { date: "Day 3", searches: 48, views: 32, pins: 2 },
    { date: "Day 4", searches: 65, views: 35, pins: 4 },
    { date: "Day 5", searches: 58, views: 30, pins: 3 },
    { date: "Day 6", searches: 42, views: 22, pins: 1 },
    { date: "Day 7", searches: 55, views: 28, pins: 3 },
  ];

  const renderScorecardsMetricsSection = () => (
    <SectionWrapper title="Scorecards and Metrics">
      <Center py="2rem">
        <Button {...primaryButtonProps}>Learn How to Improve Scores</Button>
      </Center>
    </SectionWrapper>
  );

  const renderQuadrantIndexSection = () => (
    <Box width="100%" bg={bgColor} border="1px solid" borderColor={borderColor} borderRadius="md">
      <Box pt={isMobile ? ".5rem" : "1rem"} pb={isMobile ? "0" : ".5rem"}>
        <BlockSectionHeader style={{ paddingLeft: isMobile ? ".5rem" : "1rem" }} title="Pi Quadrant Index" />
      </Box>
      <Box height={isMobile ? "15rem" : "19rem"} pl="1rem" pr={isMobile ? ".5rem" : "1rem"}>
        <QuadrantChart data={quadrantData} collectionId={collectionId} />
      </Box>
      <SectionFooter />
    </Box>
  );

  const renderCompanyScorecardSection = () => {
    if (!tickerData?.scores) return null;

    return (
      <SectionWrapper title="Company Scorecard">
        <CustomVerticalBarChart
          data={
            Array(Score).guard(tickerData.scores)
              ? tickerData.scores.map((score) => {
                  const scoreValue = score.value !== null ? score.value : 0;
                  return {
                    label: score.label,
                    value: scoreValue,
                    color: getScoreColor(scoreValue),
                  };
                })
              : Object.entries(tickerData.scores || {}).map(([scoreType, score]) => {
                  const scoreValue = score.value !== null ? score.value : 0;
                  return {
                    label: scoreType.charAt(0).toUpperCase() + scoreType.slice(1).replace(/([A-Z])/g, " $1"),
                    value: scoreValue,
                    color: getScoreColor(scoreValue),
                  };
                })
          }
          yAxisType="category"
          chartId="companyDetailScorecardBarChart"
          height="18rem"
          chartHeight={"100%"}
        />
      </SectionWrapper>
    );
  };

  if (isLoading) {
    return (
      <Center height="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <SectionContentLayout useGrid className="ch-company-dashboard-sections">
      {/* Left Column */}
      <Stack spacing={isMobile ? ".5rem" : "1rem"}>
        <SectionInvestorReadiness score={mockInvestorReadinessScore} showMoreAbout />
        <SectionNetworkActivity networkActivityData={mockNetworkActivityData} />
        <SectionInvestmentSummary investmentSummaryData={investmentSummaryData} />
      </Stack>

      {/* Right Column */}
      <Stack spacing={isMobile ? ".5rem" : "1rem"}>
        {renderScorecardsMetricsSection()}
        {renderQuadrantIndexSection()}
        {renderCompanyScorecardSection()}
      </Stack>
    </SectionContentLayout>
  );
};
